import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getApiBaseUrl, API_ENDPOINTS } from '../config/api.config';
import { authService } from './auth.service';
import analytics from '@/utils/analytics';
import { addBreadcrumb, captureApiError } from '@/utils/sentry';

// Read CSRF token from sessionStorage (stored from login/refresh/me response body)
// Cookie-based reading doesn't work cross-subdomain (e.g., nightly.skillzen.ai can't read nightly-api.skillzen.ai cookies)
function getCsrfToken(): string | null {
  return sessionStorage.getItem('skillzen_csrf_token');
}

const apiClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  headers: {
    'Accept': 'application/json'
  },
});

// Request interceptor to add auth token and tenant key
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add request start time for performance tracking
    (config as any).metadata = { startTime: Date.now() };

    // Add CSRF token for state-changing requests (server validates via HMAC)
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    // Add request ID for tracking
    config.headers['x-request-id'] = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();

    // Set appropriate Content-Type based on data type
    if (config.data instanceof FormData) {
      // Remove Content-Type for FormData - let browser set it with boundary
      delete config.headers['Content-Type'];
    } else if (config.data && typeof config.data === 'object') {
      // Set JSON content type for object data
      config.headers['Content-Type'] = 'application/json';
    }

    // Sentry breadcrumb for outgoing request
    addBreadcrumb('http', 'API Request', {
      method: (config.method || 'GET').toUpperCase(),
      url: config.url,
    });

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and errors
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(undefined);
    }
  });

  failedQueue = [];
};

// CSRF recovery state
let isRecoveringCsrf = false;
let csrfFailedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processCsrfQueue = (error: any) => {
  csrfFailedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(undefined);
    }
  });
  csrfFailedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => {
    // Track successful API performance
    const config = response.config as any;
    if (config.metadata?.startTime) {
      const duration = Date.now() - config.metadata.startTime;
      const endpoint = config.url || 'unknown';
      const method = (config.method || 'GET').toUpperCase();
      analytics.apiPerformance(endpoint, method, duration, response.status, true);
    }

    // Check for API-level errors (success: false in response body)
    // Some APIs return HTTP 200 but indicate failure in the body
    if (response.data && response.data.success === false) {
      const error = new Error(
        response.data.error?.error_description ||
        response.data.error?.message ||
        response.data.message ||
        'Request failed'
      ) as any;
      error.response = response;
      error.isApiError = true;
      return Promise.reject(error);
    }

    return response;
  },
  async (error: AxiosError) => {
    // Track failed API performance
    const config = error.config as any;
    if (config?.metadata?.startTime) {
      const duration = Date.now() - config.metadata.startTime;
      const endpoint = config.url || 'unknown';
      const method = (config.method || 'GET').toUpperCase();
      const status = error.response?.status || 0;
      analytics.apiPerformance(endpoint, method, duration, status, false);

      // Track API errors
      if (status >= 400) {
        analytics.error(
          `API_${status}`,
          `${method} ${endpoint} failed`,
          'api_client',
          status >= 500 ? 'high' : 'medium'
        );
      }

      // Sentry: breadcrumb for all API errors, capture 5xx as exceptions
      addBreadcrumb('http', 'API Error', { method, url: endpoint, status, duration }, 'error');
      if (status >= 500) {
        captureApiError(error, { duration });
      }
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _csrfRetry?: boolean };
    if (!originalRequest) return Promise.reject(error);

    const status = error.response?.status;

    // ========== 403 CSRF HANDLER — check BEFORE 401 ==========
    const errorData = error.response?.data as any;
    const errorCode = errorData?.error?.error || errorData?.error;
    const errorDesc = errorData?.error?.error_description || errorData?.message || '';

    // Tenant errors — handle separately, no recovery
    if (status === 403 && (errorCode === 'tenant_inactive' || errorCode === 'tenant_suspended')) {
      authService.clearSession();
      if (!window.location.pathname.includes('/tenant-blocked')) {
        window.location.replace('/tenant-blocked');
      }
      return Promise.reject(error);
    }

    // CSRF mismatch detection:
    // 1. 403 + error code/description mentions CSRF
    // 2. CORS-blocked 403 (Network Error fallback)
    const isCsrfError = status === 403 && (
      errorCode === 'csrf_token_missing'
      || errorCode === 'csrf_token_invalid'
      || errorDesc.includes('CSRF')
    );

    const isNetworkCsrfError = !error.response
      && error.message === 'Network Error'
      && originalRequest.method !== 'get'
      && getCsrfToken();

    if ((isCsrfError || isNetworkCsrfError) && !originalRequest._csrfRetry) {
      originalRequest._csrfRetry = true;

      if (isRecoveringCsrf) {
        return new Promise((resolve, reject) => {
          csrfFailedQueue.push({ resolve, reject });
        }).then(() => apiClient(originalRequest))
          .catch(err => Promise.reject(err));
      }

      isRecoveringCsrf = true;

      try {
        let csrf: string | undefined;

        // Try GET /me first — works if access token is still valid
        try {
          const meRes = await axios.get(
            `${getApiBaseUrl()}${API_ENDPOINTS.AUTH.ME}`,
            { withCredentials: true }
          );
          csrf = meRes.data?.data?.csrf_token;
        } catch {
          // /me failed (likely 401 — expired access token)
          // Refresh first (CSRF-exempt), then /me again
          try {
            await axios.post(
              `${getApiBaseUrl()}${API_ENDPOINTS.AUTH.REFRESH}`,
              {},
              { withCredentials: true }
            );
            const meRetry = await axios.get(
              `${getApiBaseUrl()}${API_ENDPOINTS.AUTH.ME}`,
              { withCredentials: true }
            );
            csrf = meRetry.data?.data?.csrf_token;
          } catch {
            throw new Error('CSRF recovery failed');
          }
        }

        if (csrf) {
          authService.setCsrfToken(csrf);
        }
        processCsrfQueue(null);
        return apiClient(originalRequest);
      } catch {
        processCsrfQueue(new Error('CSRF recovery failed'));
        const signedOutPath = authService.getSignedOutPath();
        authService.clearSession();
        window.history.replaceState(null, '', signedOutPath);
        window.location.replace(signedOutPath);
        return Promise.reject(error);
      } finally {
        isRecoveringCsrf = false;
      }
    }

    // ========== 401 HANDLER ==========
    if (status === 401 && !originalRequest._retry) {
      // Don't try to refresh for auth endpoints
      if (originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/refresh') ||
          originalRequest.url?.includes('/register/')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          // Retry with updated cookies (tokens are in HttpOnly cookies)
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshed = await authService.refreshAccessToken();

        if (refreshed) {
          processQueue(null);

          // Retry the original request (cookies are updated by server)
          return apiClient(originalRequest);
        } else {
          // Refresh failed, redirect to login with history replacement
          processQueue(new Error('Token refresh failed'));
          const signedOutPath = authService.getSignedOutPath();
          authService.clearSession();
          window.history.replaceState(null, '', signedOutPath);
          window.location.replace(signedOutPath);
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError);
        const signedOutPath = authService.getSignedOutPath();
        authService.clearSession();
        window.history.replaceState(null, '', signedOutPath);
        window.location.replace(signedOutPath);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ========== OTHER ERRORS ==========
    if (status === 429) {
      console.error('Rate limit exceeded:', error.response?.data);
    }

    return Promise.reject(error);
  }
);


export default apiClient;
